import React, { useEffect, useState, useCallback } from 'react';
import { 
  Plus, 
  Search, 
  Eye, 
  Edit, 
  Trash2,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  FileText
} from 'lucide-react';
import NewCustomerModal from './NewCustomerModal';
import EditCustomerModal from './EditCustomerModal';
import ViewCustomerModal from './ViewCustomerModal';
import apiService from '../../services/api';
import { showSuccess, showError, showDeleteConfirm } from '../../utils/toast';
import { useAuth } from '../Auth/AuthContext';
import { hasPermission } from '../../utils/permissions';
import PaginationControls from '../common/PaginationControls';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';

const Customers = () => {
  const { user } = useAuth();
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [showViewCustomerModal, setShowViewCustomerModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm, 400);
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [total, setTotal] = useState(0);
  const [overviewStats, setOverviewStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    suspended: 0,
  });
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const mapCustomer = useCallback((c) => ({
    id: c.id,
    firstName: c.first_name,
    lastName: c.last_name,
    email: c.email,
    phone: c.phone,
    address: c.address,
    dateOfBirth: c.date_of_birth || '',
    nationality: c.nationality || '',
    passportNumber: c.passport_number || '',
    gender: c.gender || '',
    occupation: c.occupation || '',
    city: c.city || '',
    state: c.state || '',
    country: c.country || '',
    emergencyContact: c.emergency_contact || '',
    emergencyPhone: c.emergency_phone || '',
    notes: c.notes || '',
    status: c.status ? c.status.charAt(0).toUpperCase() + c.status.slice(1) : 'Active',
    totalApplications: 0,
    lastVisit: c.updated_at ? c.updated_at.split('T')[0] : '',
    createdAt: c.created_at,
    statusColor:
      c.status === 'active'
        ? 'bg-green-100 text-green-800'
        : c.status === 'inactive'
          ? 'bg-gray-100 text-gray-800'
          : c.status === 'suspended'
            ? 'bg-red-100 text-red-800'
            : 'bg-green-100 text-green-800',
  }), []);

  const loadCustomerStats = useCallback(async () => {
    try {
      const res = await apiService.getCustomerStats();
      const s = res.stats || {};
      setOverviewStats({
        total: parseInt(s.total, 10) || 0,
        active: parseInt(s.active, 10) || 0,
        inactive: parseInt(s.inactive, 10) || 0,
        suspended: parseInt(s.suspended, 10) || 0,
      });
    } catch (e) {
      console.error('Customer stats error:', e);
    }
  }, []);

  useEffect(() => {
    loadCustomerStats();
  }, [loadCustomerStats]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter]);

  const loadCustomers = useCallback(async (targetPage = page) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: targetPage,
        limit: pageSize,
        status: statusFilter,
      };
      if (debouncedSearch.trim()) {
        params.search = debouncedSearch.trim();
      }

      const data = await apiService.getCustomers(params);
      const pagination = data.pagination || {};
      const listTotal = pagination.total ?? 0;
      setTotal(listTotal);

      const tp = pagination.pages || Math.max(1, Math.ceil(listTotal / pageSize));
      if (targetPage > tp && tp >= 1) {
        setPage(tp);
      }

      const list = data.customers || [];
      setCustomers(list.map(mapCustomer));
    } catch (err) {
      console.error('Failed to load customers', err);
      setError(err.message || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, statusFilter, mapCustomer]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'suspended', label: 'Suspended' }
  ];

  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
    setShowViewCustomerModal(true);
  };

  const handleEditCustomer = (customer) => {
    setSelectedCustomer(customer);
    setShowEditCustomerModal(true);
  };

  const handleDeleteCustomer = async (customer) => {
    const confirmed = await showDeleteConfirm(
      `${customer.firstName} ${customer.lastName}`,
      'customer'
    );
    
    if (!confirmed) return;
    
    try {
      await apiService.deleteCustomer(customer.id);
      showSuccess('Customer deleted successfully');
      await loadCustomerStats();
      await loadCustomers();
    } catch (err) {
      showError(err.message || 'Failed to delete customer');
    }
  };

  const handleSaveCustomer = async (payload) => {
    try {
      await apiService.updateCustomer(payload.id, {
        first_name: payload.firstName,
        last_name: payload.lastName,
        email: payload.email,
        phone: payload.phone,
        address: payload.address,
        date_of_birth: payload.dateOfBirth,
        nationality: payload.nationality,
        passport_number: payload.passportNumber,
        gender: payload.gender,
        occupation: payload.occupation,
        city: payload.city,
        state: payload.state,
        country: payload.country,
        emergency_contact: payload.emergencyContact,
        emergency_phone: payload.emergencyPhone,
        notes: payload.notes,
        status: payload.status ? payload.status.toLowerCase() : 'active'
      });
      showSuccess('Customer updated successfully');
      setShowEditCustomerModal(false);
      setSelectedCustomer(null);
      await loadCustomerStats();
      await loadCustomers();
    } catch (err) {
      showError(err.message || 'Failed to save customer');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Customers</h1>
          <p className="text-gray-600 dark:text-gray-400">
            {user?.role === 'agent' 
              ? 'Manage your customers and track their applications'
              : 'Manage customer information and track their applications'
            }
          </p>
        </div>
        {(hasPermission(user, 'customers.create') || user?.role === 'agent') && (
          <button
            onClick={() => setShowNewCustomerModal(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>Add Customer</span>
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card text-center">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{overviewStats.total}</div>
          <div className="text-sm text-gray-600">Total Customers</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-green-600">{overviewStats.active}</div>
          <div className="text-sm text-gray-600">Active</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-gray-600">{overviewStats.inactive}</div>
          <div className="text-sm text-gray-600">Inactive</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-red-600">{overviewStats.suspended}</div>
          <div className="text-sm text-gray-600">Suspended</div>
        </div>
      </div>

      <div className="card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <div className="md:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field"
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error && <div className="card text-red-600">{error}</div>}
      {loading ? (
        <div className="card">Loading customers...</div>
      ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {customers.length === 0 ? (
          <div className="col-span-full card text-center text-gray-500 py-12">
            No customers match your filters.
          </div>
        ) : (
        customers.map((customer) => (
          <div key={customer.id} className="card hover:shadow-md transition-shadow duration-200">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
                  <User className="h-6 w-6 text-primary-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {customer.firstName} {customer.lastName}
                  </h3>
                  <p className="text-sm text-gray-500">{customer.id}</p>
                </div>
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${customer.statusColor}`}>
                {customer.status}
              </span>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <Mail className="h-4 w-4 mr-2" />
                {customer.email || 'No email provided'}
              </div>
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <Phone className="h-4 w-4 mr-2" />
                {customer.phone}
              </div>
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <MapPin className="h-4 w-4 mr-2" />
                <span className="truncate">{customer.address}</span>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
              <div className="flex items-center">
                <FileText className="h-4 w-4 mr-1" />
                {customer.totalApplications} applications
              </div>
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                Last visit: {customer.lastVisit}
              </div>
            </div>

            <div className="flex space-x-2">
              {(hasPermission(user, 'customers.view') || user?.role === 'agent') && (
                <button 
                  onClick={() => handleViewCustomer(customer)}
                  className="flex-1 btn-outline text-sm"
                >
                  <Eye className="h-4 w-4 mr-1" />
                  View
                </button>
              )}
              {(hasPermission(user, 'customers.edit') || user?.role === 'agent') && (
                <button 
                  onClick={() => handleEditCustomer(customer)}
                  className="flex-1 btn-secondary text-sm"
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </button>
              )}
              {(hasPermission(user, 'customers.delete') || user?.role === 'agent') && (
                <button 
                  onClick={() => handleDeleteCustomer(customer)}
                  className="px-3 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors duration-200"
                  title="Delete Customer"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))
        )}
      </div>
      <div className="card px-0 py-0 overflow-hidden">
        <PaginationControls
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(n) => {
            setPageSize(n);
            setPage(1);
          }}
          pageSizeOptions={[6, 12, 24, 48]}
        />
      </div>
        </>
      )}

      {showNewCustomerModal && (
        <NewCustomerModal
          onClose={() => setShowNewCustomerModal(false)}
          onSave={async (data) => {
            try {
              await apiService.createCustomer({
                first_name: data.firstName,
                last_name: data.lastName,
                email: data.email && data.email.trim() !== '' ? data.email.trim() : null,
                phone: data.phone,
                address: data.address,
                date_of_birth: data.dateOfBirth,
                nationality: data.nationality,
                passport_number: data.passportNumber,
                gender: data.gender,
                occupation: data.occupation,
                city: data.city,
                state: data.state,
                country: data.country,
                emergency_contact: data.emergencyContact,
                emergency_phone: data.emergencyPhone,
                notes: data.notes
              });
              showSuccess('Customer created successfully');
              setShowNewCustomerModal(false);
              setPage(1);
              await loadCustomerStats();
              await loadCustomers(1);
            } catch (err) {
              showError(err.message || 'Failed to create customer');
            }
          }}
        />
      )}

      {showEditCustomerModal && selectedCustomer && (
        <EditCustomerModal
          customer={selectedCustomer}
          onClose={() => {
            setShowEditCustomerModal(false);
            setSelectedCustomer(null);
          }}
          onSave={handleSaveCustomer}
        />
      )}

      {showViewCustomerModal && selectedCustomer && (
        <ViewCustomerModal
          customer={selectedCustomer}
          onClose={() => {
            setShowViewCustomerModal(false);
            setSelectedCustomer(null);
          }}
        />
      )}
    </div>
  );
};

export default Customers;
